import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { Navbar, Container, Form, Nav, Dropdown } from 'react-bootstrap';

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const Logo: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => (
    <img
      src="/logo.png"
      alt="NayaBajar"
      style={{ height: size === 'sm' ? 32 : 38, width: 'auto', display: 'block' }}
    />
  );

  return (
    <>
      {/* Desktop Header */}
      <Navbar
        expand="lg"
        className="d-none d-lg-block py-2"
        style={{
          background: 'rgba(255, 249, 244, 0.97)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(244, 223, 204, 0.8)',
          boxShadow: '0 2px 12px rgba(116, 67, 35, 0.05)',
          zIndex: 100,
        }}
      >
        <Container fluid>
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center py-1">
            <Logo />
          </Navbar.Brand>

          {/* Search Bar */}
          <Form onSubmit={handleSearch} className="flex-1 mx-4 d-none d-lg-block" style={{ maxWidth: 520 }}>
            <div
              className="position-relative d-flex align-items-center"
              style={{
                background: 'var(--pw-surface)',
                border: '1.5px solid var(--pw-border)',
                borderRadius: 16,
                padding: '4px 6px 4px 4px',
                transition: 'all 0.2s ease',
                boxShadow: 'inset 0 1px 2px rgba(116, 67, 35, 0.04)',
              }}
            >
              <MagnifyingGlassIcon
                className="text-muted ms-3"
                style={{ width: 20, height: 20, flexShrink: 0 }}
              />
              <Form.Control
                type="text"
                placeholder="Search for mobiles, cars, properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  boxShadow: 'none',
                  padding: '10px 14px',
                  fontSize: 15,
                  color: 'var(--pw-text)',
                }}
              />
              <button
                type="submit"
                className="d-flex align-items-center justify-content-center border-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <MagnifyingGlassIcon style={{ width: 18, height: 18 }} />
              </button>
            </div>
          </Form>

          <Nav className="ms-auto align-items-center gap-2">
            {isAuthenticated ? (
              <>
                {/* Messages */}
                <Link
                  to="/messages"
                  className="d-flex align-items-center justify-content-center position-relative"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: 'var(--pw-surface-tint)',
                    border: '1px solid var(--pw-border)',
                    color: 'var(--pw-text-soft)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--pw-bg-soft)';
                    e.currentTarget.style.color = 'var(--pw-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--pw-surface-tint)';
                    e.currentTarget.style.color = 'var(--pw-text-soft)';
                  }}
                >
                  <ChatBubbleLeftRightIcon style={{ width: 20, height: 20 }} />
                </Link>

                {/* Favorites */}
                <Link
                  to="/favorites"
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: 'var(--pw-surface-tint)',
                    border: '1px solid var(--pw-border)',
                    color: 'var(--pw-text-soft)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--pw-bg-soft)';
                    e.currentTarget.style.color = 'var(--pw-danger)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--pw-surface-tint)';
                    e.currentTarget.style.color = 'var(--pw-text-soft)';
                  }}
                >
                  <HeartIcon style={{ width: 20, height: 20 }} />
                </Link>

                {/* Sell Button */}
                <Link
                  to="/post-ad"
                  className="d-flex align-items-center gap-2 fw-semibold text-decoration-none"
                  style={{
                    padding: '10px 18px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, var(--pw-accent) 0%, #f5a623 100%)',
                    color: '#5c3d0d',
                    boxShadow: '0 4px 14px rgba(255, 191, 77, 0.3)',
                    transition: 'all 0.2s ease',
                    fontSize: 14,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(255, 191, 77, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 191, 77, 0.3)';
                  }}
                >
                  <PlusCircleIcon style={{ width: 18, height: 18 }} />
                  <span>Sell</span>
                </Link>

                {/* User Dropdown */}
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="light"
                    className="d-flex align-items-center gap-2 border-0"
                    style={{
                      background: 'var(--pw-surface-tint)',
                      borderRadius: 14,
                      padding: '6px 12px 6px 6px',
                      color: 'var(--pw-text)',
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {user?.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.displayName}
                        className="rounded-circle"
                        style={{ width: 34, height: 34, objectFit: 'cover', border: '2px solid var(--pw-border)' }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 34,
                          height: 34,
                          background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {user?.displayName?.[0] || 'U'}
                      </div>
                    )}
                    <span className="d-none d-xl-inline">{user?.displayName}</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item as={Link} to="/dashboard">Dashboard</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/profile">Profile</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/my-ads">My Ads</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/favorites">Favorites</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout} style={{ color: 'var(--pw-danger)' }}>
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn btn-outline-primary btn-sm"
                  style={{ padding: '10px 20px', fontSize: 14 }}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn btn-primary btn-sm"
                  style={{ padding: '10px 20px', fontSize: 14 }}
                >
                  Register
                </Link>
              </>
            )}
          </Nav>
        </Container>
      </Navbar>

      {/* Mobile Header */}
      <Navbar
        expand="lg"
        className="d-lg-none py-2"
        style={{
          background: 'rgba(255, 249, 244, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(244, 223, 204, 0.6)',
          zIndex: 1050,
        }}
      >
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              {location.pathname !== '/' ? (
                <button
                  onClick={() => navigate(-1)}
                  className="d-flex align-items-center justify-content-center border-0 me-2"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'var(--pw-surface-tint)',
                    color: 'var(--pw-text-soft)',
                  }}
                >
                  <ArrowLeftIcon style={{ width: 20, height: 20 }} />
                </button>
              ) : (
                <button
                  onClick={onMenuClick}
                  className="d-flex align-items-center justify-content-center border-0 me-2"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'var(--pw-surface-tint)',
                    color: 'var(--pw-text-soft)',
                  }}
                >
                  <Bars3Icon style={{ width: 20, height: 20 }} />
                </button>
              )}
              <Link to="/" className="text-decoration-none">
                <Logo size="sm" />
              </Link>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isAuthenticated && (
                <Link
                  to="/post-ad"
                  className="d-flex align-items-center justify-content-center border-0"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, var(--pw-accent) 0%, #f5a623 100%)',
                    color: '#5c3d0d',
                  }}
                >
                  <PlusCircleIcon style={{ width: 20, height: 20 }} />
                </Link>
              )}
              {!isAuthenticated && (
                <Link
                  to="/login"
                  className="btn btn-outline-primary btn-sm"
                  style={{ padding: '8px 14px', fontSize: 13 }}
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </Container>
      </Navbar>
    </>
  );
};

export default Header;
